import React, { useRef, useState, useEffect } from 'react';

/**
 * Material UI Components
 */
import {
    Fab,
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText
} from '@mui/material';

/**
 * Material UI Icons
 */
import { Add as AddIcon } from '@mui/icons-material';

const styles = {
    topFabBox: { display: 'flex', justifyContent: 'end', p: 2 },
};

/**
 * PhotoInputSelector
 * 
 * @param {Object} props
 * @param {Function} props.onFilesSelected
 * @param {string} props.buttonLabel
 * @param {boolean} props.allowMultiple
 * @param {Function} props.t
 */
const PhotoInputSelector = ({ onFilesSelected, buttonLabel = 'Add Photo', allowMultiple = true, t = (s) => s }) => {
    const [chooseDialogOpen, setChooseDialogOpen] = useState(false);
    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);
    const [hasCamera, setHasCamera] = useState(false);

    useEffect(() => {
        const checkHasCamera = async () => {
            try {
                const devices = await navigator.mediaDevices.enumerateDevices();
                const hasVideoInput = devices.some(device => device.kind === 'videoinput');

                setHasCamera(hasVideoInput);
            } catch (err) {
                console.warn('Unable to detect camera:', err);
            }
        };

        checkHasCamera();
    }, []);

    const handleChooseSource = (useCamera) => {
        setChooseDialogOpen(false);
        if (useCamera) {
            cameraInputRef.current.click();
        } else {
            fileInputRef.current.click();
        }
    };

    const handleChange = (event) => {
        const files = event?.target?.files ? Array.from(event.target.files) : [];
        if (files.length > 0) {
            // Simulate an event as expected by PhotoManager
            const fakeEvent = {
                target: { files }
            };
            onFilesSelected(fakeEvent);
        }
        if (event?.target) event.target.value = null;
    };

    return (
        <>
            <Box sx={styles.topFabBox}>
                <Fab
                    variant="extended"
                    color="primary"
                    onClick={() => {
                        if (hasCamera) {
                            setChooseDialogOpen(true);
                        } else {
                            fileInputRef.current.click();
                        }
                    }}
                    size="small"
                    sx={{ marginLeft: 'auto' }}
                >
                    <AddIcon sx={{ mr: 1 }} />
                    {t(buttonLabel)}
                </Fab>
            </Box>

            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple={allowMultiple}
                style={{ display: 'none' }}
                onChange={handleChange}
            />

            <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                multiple={allowMultiple}
                style={{ display: 'none' }}
                onChange={handleChange}
            />

            <Dialog open={chooseDialogOpen} onClose={() => setChooseDialogOpen(false)}>
                <DialogTitle>{t('Choose Image Source')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('Do you want to take a photo or choose from gallery?')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleChooseSource(false)}>{t('Gallery')}</Button>
                    <Button onClick={() => handleChooseSource(true)}>{t('Camera')}</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default PhotoInputSelector;
